import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, switchMap, startWith, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { EventActions } from './event.actions';
import { EventService } from '../../api-services/event/event.service';
import { EventProtocolsService } from '../../api-services/event/event_protocols.service';
import { EventDefinitionsService } from '../../api-services/event/event_definitions.service';
import { Store } from '@ngrx/store';
import { RootState } from '../store';
import { AppActions } from '../app/app.actions';
import { FrontEventService } from '../../local-services/front-event.service';
import { EventAccountService } from '../../api-services/event/event_account.service';

@Injectable()

export class EventEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<RootState>);

  constructor(
    private eventService: EventService,
    private eventAccountService: EventAccountService,
    private eventProtocolService: EventProtocolsService,
    private eventDefinitionService: EventDefinitionsService,
    private frontEventService: FrontEventService
  ) { }

  loadAllEvents$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadAllEvents),
      concatMap((action) =>
        this.eventService.getAllEvents(action.EventListQuery).pipe(
          switchMap(data => [
            EventActions.loadAllEventsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadAccountEvents$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadAccountEvents),
      concatMap((action) =>
        this.eventAccountService.getEventsByAccount(action.accountId, action.EventListQuery).pipe(
          switchMap(data => [
            EventActions.loadAccountEventsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadEventById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadEventById),
      concatMap((action) =>
        this.eventService.getEventById(action.eventId).pipe(
          switchMap(data => [
            EventActions.loadEventByIdSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadEventProtocols$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadEventProtocols),
      concatMap(() =>
        this.eventProtocolService.getEventProtocols().pipe(
          switchMap(data => [
            EventActions.loadEventProtocolsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  createEventProtocol$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.createEventProtocol),
      concatMap((action) =>
        this.eventProtocolService.createEventProtocol(action.payload).pipe(
          switchMap(data => [
            EventActions.createEventProtocolSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            EventActions.loadEventProtocols()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  updateEventProtocol$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.updateEventProtocol),
      concatMap((action) =>
        this.eventProtocolService.updateEventProtocol(action.payload).pipe(
          switchMap(() => [
            EventActions.updateEventProtocolSuccess(),
            AppActions.setLoading({ loading: false }),
            EventActions.loadEventProtocols()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  deleteEventProtocol$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.deleteEventProtocol),
      concatMap((action) =>
        this.eventProtocolService.deleteEventProtocol(action.id).pipe(
          switchMap(() => [
            EventActions.deleteEventProtocolSuccess({ id: action.id }),
            EventActions.loadEventProtocols(),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadEventProtocolById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadEventProtocolById),
      concatMap((action) =>
        this.eventProtocolService.getEventProtocolById(action.id).pipe(
          switchMap(data => [
            EventActions.loadEventProtocolByIdSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadEventDefinitions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadEventDefinitions),
      concatMap(() =>
        this.eventDefinitionService.getEventDefinitions().pipe(
          switchMap(data => [
            EventActions.loadEventDefinitionsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  createEventDefinition$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.createEventDefinition),
      concatMap((action) =>
        this.eventDefinitionService.createEventDefinition(action.payload).pipe(
          switchMap(data => [
            EventActions.createEventDefinitionSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            EventActions.loadEventDefinitions()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  updateEventDefinition$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.updateEventDefinition),
      concatMap((action) =>
        this.eventDefinitionService.updateEventDefinition(action.payload).pipe(
          switchMap(() => [
            EventActions.updateEventDefinitionSuccess(),
            AppActions.setLoading({ loading: false }),
            EventActions.loadEventDefinitions()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  deleteEventDefinition$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.deleteEventDefinition),
      concatMap((action) =>
        this.eventDefinitionService.deleteEventDefinition(action.id).pipe(
          switchMap(data => [
            EventActions.deleteEventDefinitionSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            EventActions.loadEventDefinitions(),
            EventActions.loadEventProtocols()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadEventDefinitionById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EventActions.loadEventDefinitionById),
      concatMap((action) =>
        this.eventDefinitionService.getEventDefinitionById(action.id).pipe(
          switchMap(data => [
            EventActions.loadEventDefinitionByIdSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  filterNotificableEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventActions.filterNotificableEvents),
      concatMap(({ payload }) =>
        this.frontEventService.filterNotificableEvents(payload).pipe(
          map(data => EventActions.filterNotificableEventsSuccess({ data })),
          catchError(error => of(AppActions.setError({ error })))
        )
      )
    )
  );
}
